import React, { useState, useEffect, lazy, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { supabase } from '../../../lib/supabase';

const GlobeAnimation = lazy(() => import('../../../components/ui/GlobeAnimation'));
const FloatingCards = lazy(() => import('../../../components/ui/FloatingCards'));

const HeroSection = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchType, setSearchType] = useState('all');
  const [stats, setStats] = useState({ companies: 0, individuals: 0, links: 0, revenue: 0 });
  const navigate = useNavigate();

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [
          { count: companyCount },
          { count: individualCount },
          { count: execCount },
          { count: investCount },
          { count: pacCount },
          { data: revenueData },
        ] = await Promise.all([
          supabase.from('companies').select('*', { count: 'exact', head: true }),
          supabase.from('user_profiles').select('*', { count: 'exact', head: true }),
          supabase.from('executives').select('*', { count: 'exact', head: true }),
          supabase.from('investments').select('*', { count: 'exact', head: true }),
          supabase.from('pac_relationships').select('*', { count: 'exact', head: true }),
          supabase.from('companies').select('revenue'),
        ]);
        const totalRevenue = (revenueData || []).reduce((sum, c) => sum + (c.revenue || 0), 0);
        setStats({
          companies: companyCount || 0,
          individuals: individualCount || 0,
          links: (execCount || 0) + (investCount || 0) + (pacCount || 0),
          revenue: totalRevenue,
        });
      } catch (err) {
        console.error('HeroSection stats error:', err);
      }
    };
    fetchStats();
  }, []);

  const handleSearch = (e) => {
    e?.preventDefault();
    if (searchQuery?.trim()) {
      navigate(`/search-results?q=${encodeURIComponent(searchQuery)}&type=${searchType}`);
    }
  };

  const searchSuggestions = [
    { text: 'Elon Musk', type: 'individual' },
    { text: 'Apple Inc.', type: 'company' },
    { text: 'Jeff Bezos', type: 'individual' },
    { text: 'Microsoft Corporation', type: 'company' }
  ];

  return (
    <div className="relative py-16 px-4 overflow-hidden min-h-screen">
      {/* Futuristic 3D Globe Background (Three.js WebGL) */}
      <div className="absolute inset-0">
        {/* Deep space gradient base */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#020a18] via-[#051225] to-[#0a1628]"></div>
        
        {/* Three.js Globe Canvas */}
        <Suspense fallback={
          <div className="absolute inset-0 bg-gradient-to-br from-[#020a18] via-[#051225] to-[#0a1628]" />
        }>
          <GlobeAnimation />
        </Suspense>
        
        {/* Dark overlay to ensure text readability */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-black/50"></div>

        {/* Floating mini-cards that pop in/out */}
        <Suspense fallback={null}>
          <FloatingCards />
        </Suspense>
      </div>
      {/* Content */}
      <div className="relative z-10 max-w-4xl mx-auto text-center">
        {/* Main Heading */}
        <div className="mb-8 pt-16">
          <h1 className="font-heading font-bold text-5xl lg:text-7xl mb-6 whitespace-nowrap">
            <span className="text-white drop-shadow-2xl font-black filter brightness-110">
              Who You Makin' Rich?
            </span>
          </h1>
          <p className="text-xl lg:text-2xl text-white max-w-3xl mx-auto leading-relaxed font-medium drop-shadow-lg">
            Discover the complete ownership structures, investors, and stakeholders behind the companies and individuals shaping our world.
          </p>
        </div>

        {/* Search Section */}
        <div className="mb-8">
          <form onSubmit={handleSearch} className="max-w-2xl mx-auto">
            <div className="flex flex-col sm:flex-row gap-3 mb-4">
              <div className="flex-1">
                <Input
                  type="search"
                  placeholder="Search companies, executives, investors, politicians..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e?.target?.value)}
                  className="text-center sm:text-left rounded-[2px] border-2 focus:border-primary shadow-sm bg-white/95 backdrop-blur-sm"
                />
              </div>
              <div className="flex gap-2">
                <select
                  value={searchType}
                  onChange={(e) => setSearchType(e?.target?.value)}
                  className="px-4 py-2 bg-white/95 backdrop-blur-sm border-2 border-gray-200 rounded-[2px] text-sm focus:outline-none focus:border-primary shadow-sm"
                >
                  <option value="all">All</option>
                  <option value="individuals">Individuals</option>
                  <option value="companies">Companies</option>
                </select>
                <Button
                  type="submit"
                  variant="default"
                  iconName="Search"
                  iconPosition="left"
                  disabled={!searchQuery?.trim()}
                  className="rounded-[2px] px-6 shadow-sm"
                >
                  Search
                </Button>
              </div>
            </div>
          </form>

          {/* Quick Search Suggestions */}
          <div className="flex flex-wrap justify-center gap-2">
            <span className="text-sm text-white/80 font-medium">Try searching:</span>
            {searchSuggestions?.map((suggestion, index) => (
              <button
                key={index}
                onClick={() => navigate(`/search-results?q=${encodeURIComponent(suggestion?.text)}&type=${suggestion?.type === 'individual' ? 'individuals' : 'companies'}`)}
                className="inline-flex items-center px-3 py-1 bg-white/90 hover:bg-white border border-gray-200 rounded-[2px] text-sm text-gray-700 transition-all duration-200 shadow-sm hover:shadow-md hover:border-primary/30 backdrop-blur-sm"
              >
                <Icon 
                  name={suggestion?.type === 'individual' ? 'User' : 'Building2'} 
                  size={12} 
                  className="mr-1 text-primary" 
                />
                {suggestion?.text}
              </button>
            ))}
          </div>
        </div>

        {/* Live Stats Section */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 max-w-5xl mx-auto mb-8">
          <div className="text-center bg-white/80 backdrop-blur-sm rounded-[2px] p-4 shadow-lg border border-white/40">
            <div className="text-2xl lg:text-3xl font-bold text-primary mb-1">
              {stats.companies > 200 ? '200+' : stats.companies > 0 ? stats.companies.toLocaleString() : '—'}
            </div>
            <div className="text-xs text-gray-700 font-medium">Companies Tracked</div>
          </div>
          <div className="text-center bg-white/80 backdrop-blur-sm rounded-[2px] p-4 shadow-lg border border-white/40">
            <div className="text-2xl lg:text-3xl font-bold text-emerald-600 mb-1">
              {stats.individuals > 500 ? '600+' : stats.individuals > 0 ? stats.individuals.toLocaleString() : '—'}
            </div>
            <div className="text-xs text-gray-700 font-medium">Individuals Profiled</div>
          </div>
          <div className="text-center bg-white/80 backdrop-blur-sm rounded-[2px] p-4 shadow-lg border border-white/40">
            <div className="text-2xl lg:text-3xl font-bold text-amber-600 mb-1">
              {stats.revenue >= 1e12
                ? `$${(stats.revenue / 1e12).toFixed(1)}T`
                : stats.revenue >= 1e9
                ? `$${(stats.revenue / 1e9).toFixed(0)}B`
                : '—'}
            </div>
            <div className="text-xs text-gray-700 font-medium">Revenue Tracked</div>
          </div>
          <div className="text-center bg-white/80 backdrop-blur-sm rounded-[2px] p-4 shadow-lg border border-white/40">
            <div className="text-2xl lg:text-3xl font-bold text-blue-600 mb-1">
              {stats.links > 0 ? stats.links.toLocaleString() : '—'}
            </div>
            <div className="text-xs text-gray-700 font-medium">Connections Mapped</div>
          </div>
          <div className="col-span-2 lg:col-span-1 text-center bg-white/80 backdrop-blur-sm rounded-[2px] p-4 shadow-lg border border-white/40">
            <div className="text-lg lg:text-xl font-bold text-purple-600 mb-1">Real-time</div>
            <div className="text-xs text-gray-700 font-medium">Data Updates</div>
          </div>
        </div>

        {/* Trust Indicators */}
        <div className="flex flex-wrap justify-center items-center gap-6 text-sm text-white">
          <div className="flex items-center space-x-2 bg-white/20 backdrop-blur-sm px-3 py-2 rounded-[2px] border border-white/30">
            <Icon name="Shield" size={16} className="text-green-400" />
            <span className="font-medium">SSL Secured</span>
          </div>
          <div className="flex items-center space-x-2 bg-white/20 backdrop-blur-sm px-3 py-2 rounded-[2px] border border-white/30">
            <Icon name="Database" size={16} className="text-blue-400" />
            <span className="font-medium">Government Sources</span>
          </div>
          <div className="flex items-center space-x-2 bg-white/20 backdrop-blur-sm px-3 py-2 rounded-[2px] border border-white/30">
            <Icon name="Globe" size={16} className="text-cyan-400" />
            <span className="font-medium">Wikipedia Verified</span>
          </div>
          <div className="flex items-center space-x-2 bg-white/20 backdrop-blur-sm px-3 py-2 rounded-[2px] border border-white/30">
            <Icon name="Clock" size={16} className="text-purple-400" />
            <span className="font-medium">Updated Daily</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HeroSection;